import type {OptimisticTask, UITask, UnhydratedTask} from '../types/task'
import type {APITask} from '../types/api-task'
import {getGitHubArtifactData} from './task-artifact-helpers'

/**
 * Gets the display title for a task.
 *
 * @param task - The task to get the title for
 * @returns The display title for the task
 */
export function getTaskTitle(task: UITask): string {
  if (task.pull?.title) {
    return task.pull.title
  }

  if (task.name) {
    return task.name
  }

  return 'New task'
}

/**
 * Transforms a single API Task to UnhydratedTask format.
 * Used by the mutation to insert the real task into the cache after creation.
 * Returns null if the task has no GitHub artifact (can't be displayed).
 */
export function transformAPITaskToUnhydrated(apiTask: APITask): UnhydratedTask | null {
  const githubArtifact = getGitHubArtifactData(apiTask.artifacts)
  const agentId = apiTask.agent_collaborators?.[0]?.agent_id?.toString()

  return {
    id: apiTask.id,
    resource_type: githubArtifact?.type,
    resource_id: githubArtifact?.id,
    resource_global_id: githubArtifact?.global_id,
    session_count: apiTask.session_count,
    latest_session_state: apiTask.status,
    latest_session_updated_at: apiTask.last_updated_at,
    agentId,
    name: apiTask.name,
    task_id: apiTask.id,
    archived_at: apiTask.archived_at,
    creator_id: apiTask.creator_id,
  } as UnhydratedTask
}

/**
 * Transforms an array of API Tasks to UnhydratedTask format.
 * Filters out tasks that have no GitHub artifact.
 */
export function transformAPITasksToUnhydrated(apiTasks: APITask[]): UnhydratedTask[] {
  return apiTasks.map(transformAPITaskToUnhydrated).filter((task): task is UnhydratedTask => task !== null)
}

/**
 * Merges server-fetched tasks with existing optimistic tasks from the cache.
 *
 * This function handles the transition from optimistic tasks (created client-side before
 * server confirmation) to server tasks:
 * 1. Identifies server tasks that match optimistic tasks via event_identifiers
 * 2. Preserves display metadata from optimistic tasks on their server counterparts
 * 3. Filters out optimistic tasks that now have server equivalents
 * 4. Returns a sorted array of all tasks (newest first)
 *
 * @param serverTasks - Tasks fetched from the server
 * @param optimisticTasks - Optimistic tasks from the local cache
 * @returns Merged and sorted array of tasks
 */
export function mergeServerTasksWithOptimistic(
  serverTasks: UnhydratedTask[],
  optimisticTasks: OptimisticTask[],
): Array<UnhydratedTask | OptimisticTask> {
  // Transform server tasks, marking those that replace optimistic tasks
  const serverTasksWithOptimistic = serverTasks.map(serverTask => {
    const matchingOptimisticTask = optimisticTasks.find(optimisticTask =>
      serverTask.event_identifiers?.includes(optimisticTask.clientGeneratedId),
    )

    return {
      ...serverTask,
      isOptimistic: !!matchingOptimisticTask,
      // Preserve original creation parameters from optimistic task for display
      ...(matchingOptimisticTask && {
        problemStatement: matchingOptimisticTask.problemStatement,
        nwo: matchingOptimisticTask.nwo,
        baseRef: matchingOptimisticTask.baseRef,
        clientGeneratedId: matchingOptimisticTask.clientGeneratedId,
        // Preserve name from optimistic task if server hasn't provided one yet
        name: serverTask.name ?? matchingOptimisticTask.name,
      }),
    }
  })

  // Filter out optimistic tasks that now have server equivalents
  const remainingOptimisticTasks = optimisticTasks.filter(
    optimisticTask =>
      !serverTasksWithOptimistic.some(serverTask =>
        serverTask.event_identifiers?.includes(optimisticTask.clientGeneratedId),
      ),
  )

  // Combine and sort all tasks
  return sortTasksByDate([...serverTasksWithOptimistic, ...remainingOptimisticTasks])
}

/**
 * Task with a latest_session_updated_at timestamp for sorting.
 */
interface TaskWithTimestamp {
  latest_session_updated_at: string
}

/**
 * Sorts tasks by latest_session_updated_at (newest first).
 * This is a generic utility that works with any task type that has a timestamp.
 *
 * @param tasks - Array of tasks to sort
 * @returns Sorted array (does not mutate the original)
 */
export function sortTasksByDate<T extends TaskWithTimestamp>(tasks: T[]): T[] {
  return [...tasks].sort(
    (a, b) => new Date(b.latest_session_updated_at).getTime() - new Date(a.latest_session_updated_at).getTime(),
  )
}
